import React, { useEffect, useState } from "react";
import { ShowProps, SimpleForm, useDataProvider } from "react-admin";
import { Paper, Box, Backdrop } from "@material-ui/core";
import { useStyles } from "./style";
import RoutePlanMap from "./components/RoutePlanMap";
import { useDispatch } from "react-redux";
import { changeSelectedRoute } from "../../redux/navmode.action";
import PermissionLoading from "../../components/PermissionLoading";
import { colors, processRoutesToAddSchoolLocation } from "./tools";
import HeaderContent from "../../components/layout/components/HeaderContent";
import WaveInformationSection from "./components/WaveInformationSection";
import RoutesSection from "./components/RoutesSection";
import CustomSplit from "../../components/CustomSplit";
import LoadingProgressComponent from "./components/LoadingProgressComponent";

const EnhancedRoutePlanShow = (props: ShowProps) => {
  const { id } = props;
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<any>(null);

  const [isLoadingMap, setIsLoadingMap] = useState<boolean>(false);
  const [progressMap, setProgressMap] = useState(1);

  const provider = useDataProvider();

  const dispatch = useDispatch();

  const breadcrumb = [
    { link: "/enhancedBusSchedulePlans", title: "AI Route Plans" },
    { link: `/enhancedBusSchedulePlans/${id}/show`, title: id },
  ];

  const getBusSchedulePlanData = async () => {
    let data: any = {};

    setLoading(true);
    const { data: busSchedulePlanData } = await provider.getOne(
      "busSchedulePlans",
      {
        id,
      },
    );

    data.id = busSchedulePlanData.id;
    data.createdAt = busSchedulePlanData.createdAt;
    data.updatedAt = busSchedulePlanData.updatedAt;
    data.nextEffectiveDate = busSchedulePlanData.nextEffectiveDate;

    const { data: busScheduleData } = await provider.getOne("busSchedules", {
      id: busSchedulePlanData.busScheduleId,
    });
    data.busSchedule = busScheduleData;

    const { data: schoolData } = await provider.getOne("schools", {
      id: busScheduleData.schoolId,
    });
    data.school = schoolData;

    data.busScheduleId = busSchedulePlanData.busScheduleId;
    data.nextEffectiveDate = busSchedulePlanData.nextEffectiveDate;

    const transformRoutes = processRoutesToAddSchoolLocation(
      busSchedulePlanData.routePlan.routes,
      busScheduleData,
      schoolData,
    );
    data.routes = transformRoutes.map((route: any, index: number) => {
      return {
        ...route,
        routeName: route.name,
        routeColor: colors[index % colors.length], // if colors are not enough, roll over the colors array to first index
        routeLocations: route.routeLocations.map((loc: any, index: number) => {
          let locationCodeIndex;

          if (busScheduleData.type === "to_school") {
            locationCodeIndex = index;
          } else {
            locationCodeIndex = index - 1;
          }

          // convert ASCII Code into Alphabet
          // 65 === A
          const locationCode = String.fromCharCode(65 + locationCodeIndex);

          return {
            ...loc,
            studentIds: loc.students
              ? loc.students.map((student: any) => student.id)
              : [],
            students: loc.students.map((student: any, index: number) => {
              return {
                ...student,
                locationCode: `${locationCode}${index + 1}`,
              };
            }),
          };
        }),
      };
    });
    data.droppedLocations = [];

    setLoading(false);
    return data;
  };

  useEffect(() => {
    dispatch(changeSelectedRoute(null));

    getBusSchedulePlanData()
      .then((data) => {
        setFormData(data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  if (loading) {
    return (
      <Box height="500px">
        <PermissionLoading />
      </Box>
    );
  }

  return (
    <>
      <HeaderContent
        breadcrumb={breadcrumb}
        headerTitle={`AI Route Plan #${id}`}
      />

      <Paper style={{ marginTop: "18px" }}>
        <SimpleForm
          className={classes.simpleForm}
          initialValues={{
            routes: [],
            droppedLocations: [],
          }}
          record={formData}
          toolbar={null}
        >
          <CustomSplit
            sizes={[50, 50]}
            minSize={[700, 300]}
            expandToMin={false}
            gutterSize={10}
            gutterAlign="center"
            snapOffset={30}
            dragInterval={1}
            direction="horizontal"
            cursor="col-resize"
          >
            <div style={{ padding: "24px 34px" }}>
              <WaveInformationSection />
              <RoutesSection />
            </div>
            <div style={{ padding: 0 }}>
              <RoutePlanMap
                mode="show"
                setIsLoadingMap={setIsLoadingMap}
                setProgressMap={setProgressMap}
              />
            </div>
          </CustomSplit>

          <Backdrop className={classes.backdropLoading} open={isLoadingMap}>
            <LoadingProgressComponent value={progressMap} />
          </Backdrop>
        </SimpleForm>
      </Paper>
    </>
  );
};

export default EnhancedRoutePlanShow;
